
import type { ComponentProps } from 'react';
import { useCallback, useMemo } from 'react';
import { useRouter } from 'next/router';

import type Filters from '~/domains/common/components/Filters/Filters';


import useFieldDescriptors from './useLobbyFilterDescriptors';


function useEntriesFiltersProps(isAdmin: boolean): ComponentProps<typeof Filters> {
  const router = useRouter();
  const { query, replace } = router;

  const FilterDescriptors = useFieldDescriptors();

  const filtersDescriptorsForStatus = useMemo(() =>
    [
      FilterDescriptors.SORT_BY,
      FilterDescriptors.GAME_TYPE,
      FilterDescriptors.PAYOUT_FORMATS,
      FilterDescriptors.ENTRY_TYPES,
      FilterDescriptors.ENTRY_FEE,
      ...(isAdmin ? [FilterDescriptors.HIDE_UNLISTED] : []),
      FilterDescriptors.VIEW_AS,
    ].filter(Boolean),
    [
    FilterDescriptors.SORT_BY,
    FilterDescriptors.PAYOUT_FORMATS,
    FilterDescriptors.ENTRY_TYPES,
    FilterDescriptors.ENTRY_FEE,
    FilterDescriptors.GAME_TYPE,
    FilterDescriptors.HIDE_UNLISTED,
    FilterDescriptors.VIEW_AS,
    isAdmin,
  ]);

  const values = useMemo(() => {
    if (!filtersDescriptorsForStatus) {
      return {};
    }

    return filtersDescriptorsForStatus.reduce((acc, filter) => {
      const value = filter.fromQuery(query);
      if (value) {
        acc[filter.object.key] = value;
      }

      return acc;
    }, {});
  }, [filtersDescriptorsForStatus, query]);

  const filters = useMemo(
    () =>
      filtersDescriptorsForStatus.map((filter) => {
        const activeKeyValue = values[filter.object.key];

        const getActiveLabel = () => {
          if (!activeKeyValue) return;
          const activeValues = activeKeyValue?.length;

          const activeLabelCount = activeValues > 1 && ` (${  activeValues  })`;

          if (activeValues > 1) return `${filter.object.title}${activeLabelCount}`;
          return filter.object.options.find(
            (option) => option.value === activeKeyValue?.[0]
          )?.label;
        }
        return {
          ...filter.object,
          isActive: activeKeyValue !== undefined,
          activeLabel: getActiveLabel(),
        };
      }),
    [filtersDescriptorsForStatus, values]
  );

  const handleChange = useCallback(
    (newValues: ComponentProps<typeof Filters>['values']) => {
      let newQuery = Object.fromEntries<string | string[]>(new URLSearchParams(window.location.search));

      Object.entries(newValues).forEach(([key, value]) => {
        const filter = filtersDescriptorsForStatus.find((f) => f.object.key === key);
        if (!filter) {
          return;
        }

        const queryValue = filter.toQuery(value);
        newQuery = { ...newQuery, ...queryValue };
      });

      const filteredQuery = Object.fromEntries(
        Object.entries(newQuery).filter(([_, value]) => value !== null)
      );
      replace({ query: filteredQuery }, undefined, { shallow: true });
    },
    [filtersDescriptorsForStatus, replace]
  );

  return {
    filters,
    values,
    onChange: handleChange,
  };
}

export default useEntriesFiltersProps;
